/**
 * Format a timestamp as relative time for the activity feed
 *
 * Rules:
 * - < 1 min → "just now"
 * - < 60 min → "Xm ago"
 * - < 24 hours → "Xh ago"
 * - < 7 days → "Xd ago"
 * - >= 7 days → "Dec 15" format
 */
export const formatRelativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Handle future dates or invalid timestamps
  if (diffMs < 0 || isNaN(diffMs)) {
    return 'just now';
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Less than 1 minute
  if (diffMinutes < 1) {
    return 'just now';
  }

  // Less than 60 minutes
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  // Less than 24 hours
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  // Less than 7 days
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  // 7 days or more - show absolute date
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format timestamp for accessibility (screen readers)
 * Returns full date and time
 */
export const formatAccessibleTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};
